import { useState } from "react";
import { Link } from "react-router-dom";
import { useFetch } from "@/hooks/useFetch";
import { useAuth } from "@/context/AuthContext";
import { useStudio } from "@/context/StudioContext";
import { StatCard, Panel, SectionTitle, StatusBadge, Loading, Tag } from "@/components/common";
import {
  FilmSlate, Eye, Translate, Scissors, PaperPlaneTilt, CalendarBlank, TrendUp,
  CurrencyDollar, Gauge, Warning, ShareNetwork, Plus, UsersThree, Globe, PenNib,
  MusicNotes, Package, X, Sparkle,
} from "@phosphor-icons/react";

const CREATE_OPTIONS = [
  { label: "New Episode", to: "/episodes", icon: FilmSlate },
  { label: "New Character", to: "/characters", icon: UsersThree },
  { label: "New World", to: "/worlds", icon: Globe },
  { label: "New Short", to: "/shorts", icon: Scissors },
  { label: "New Song", to: "/music", icon: MusicNotes },
  { label: "New Story", to: "/story", icon: PenNib },
  { label: "New Product Concept", to: "/merch", icon: Package },
];

export default function Home() {
  const { user } = useAuth();
  const { active } = useStudio();
  const { data: d, loading } = useFetch("/dashboard");
  const { data: eps } = useFetch("/episodes");
  const [createOpen, setCreateOpen] = useState(false);

  if (loading || !d) return <Loading />;

  return (
    <div className="animate-fadeUp">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-sm text-zinc-500 flex items-center gap-2"><span className="text-base">{active.emoji}</span>{active.name} · Command Centre</div>
          <h1 className="font-display text-3xl sm:text-5xl font-black mt-1">
            Welcome, <span className="text-magic">{(user?.name || "Owner").split(" ")[0]}</span>
          </h1>
          <p className="text-zinc-400 mt-2">Here's what's happening across {active.name} today.</p>
        </div>
        <button data-testid="create-btn" onClick={() => setCreateOpen(true)}
          className="inline-flex items-center gap-2 rounded-full bg-magic px-6 py-3 font-semibold text-black hover:opacity-90 transition-opacity self-start">
          <Plus size={20} weight="bold" /> Create
        </button>
      </div>

      {/* Top stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard testid="stat-episodes" label="Episodes in production" value={d.episodes_in_production} icon={FilmSlate} accent="#00E5FF" />
        <StatCard testid="stat-review" label="Scenes need review" value={d.scenes_requiring_review} icon={Eye} accent="#FFB800" />
        <StatCard testid="stat-translations" label="Translations ready" value={d.translations_ready} icon={Translate} accent="#00FF9D" />
        <StatCard testid="stat-shorts" label="Shorts suggested" value={d.shorts_suggested} icon={Scissors} accent="#FF007F" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Production pipeline */}
        <Panel className="lg:col-span-2">
          <SectionTitle right={<Link to="/episodes" className="text-sm text-[#00E5FF] hover:underline">View all</Link>}>
            Episodes in Production
          </SectionTitle>
          <div className="space-y-3">
            {(eps || []).slice(0, 4).map((ep) => (
              <Link key={ep.id} to="/episodes" data-testid={`home-ep-${ep.id}`}
                className="flex items-center gap-4 rounded-xl border border-white/5 bg-black/20 p-3 panel-hover">
                <img src={ep.image} alt="" className="h-14 w-20 rounded-lg object-cover" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-white truncate">{ep.title}</span>
                    <StatusBadge status={ep.stage} />
                  </div>
                  <div className="mt-2 h-1.5 w-full rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full bg-magic rounded-full" style={{ width: `${ep.progress}%` }} />
                  </div>
                </div>
                <span className="text-sm text-zinc-500 shrink-0">{ep.progress}%</span>
              </Link>
            ))}
          </div>
        </Panel>

        {/* Business snapshot */}
        <div className="space-y-4">
          <Panel>
            <SectionTitle>Business Snapshot</SectionTitle>
            <div className="space-y-3">
              <Row icon={CurrencyDollar} label="Revenue (month)" value={`$${d.revenue_month.toFixed(2)}`} color="#00FF9D" />
              <Row icon={Gauge} label="AI costs (month)" value={`$${d.ai_costs_month.toFixed(2)}`} color="#FFB800" />
              <Row icon={TrendUp} label="Estimated profit" value={`$${d.estimated_profit.toFixed(2)}`} color={d.estimated_profit >= 0 ? "#00FF9D" : "#FF3366"} />
              <Row icon={TrendUp} label="Audience growth" value={`+${d.audience_growth_pct}%`} color="#00E5FF" />
            </div>
          </Panel>
          <Panel>
            <SectionTitle>Ready to Ship</SectionTitle>
            <div className="grid grid-cols-3 gap-2 text-center">
              <MiniStat label="Publish" value={d.content_ready_to_publish} to="/publisher" icon={PaperPlaneTilt} />
              <MiniStat label="Scheduled" value={d.scheduled_posts} to="/calendar" icon={CalendarBlank} />
              <MiniStat label="Warnings" value={d.warnings} to="/notifications" icon={Warning} warn />
            </div>
          </Panel>
        </div>
      </div>

      {/* Social + warnings */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <Panel className="lg:col-span-2">
          <SectionTitle right={<Link to="/social" className="text-sm text-[#00E5FF] hover:underline">Manage</Link>}>
            Social Accounts
          </SectionTitle>
          <div className="flex items-center gap-3 rounded-xl border border-[#FFB800]/25 bg-[#FFB800]/8 p-4">
            <ShareNetwork size={22} weight="duotone" className="text-[#FFB800]" />
            <div className="flex-1">
              <div className="text-sm text-white font-medium">{d.social_connected} of {d.social_total} channels connected</div>
              <div className="text-xs text-zinc-400">Connect YouTube, Instagram & Facebook in Phase 2 to enable publishing & real analytics.</div>
            </div>
            <StatusBadge status="NOT CONNECTED" />
          </div>
        </Panel>
        <Panel>
          <SectionTitle>Recent Performance</SectionTitle>
          <p className="text-sm text-zinc-400">No real analytics yet.</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Tag>Connect YouTube</Tag><Tag>Connect Meta</Tag>
          </div>
          <Link to="/analytics" className="mt-4 inline-block text-sm text-[#00E5FF] hover:underline">View analytics →</Link>
        </Panel>
      </div>

      {/* Create modal */}
      {createOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setCreateOpen(false)} />
          <div className="relative w-full max-w-lg glass rounded-2xl p-6 animate-fadeUp">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <Sparkle size={20} weight="fill" className="text-[#00E5FF]" />
                <h3 className="font-display text-xl font-bold">Create something new</h3>
              </div>
              <button onClick={() => setCreateOpen(false)} data-testid="create-close" className="text-zinc-500 hover:text-white"><X size={20} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {CREATE_OPTIONS.map((o) => {
                const I = o.icon;
                return (
                  <Link key={o.label} to={o.to} onClick={() => setCreateOpen(false)}
                    data-testid={`create-${o.label.toLowerCase().replace(/ /g, "-")}`}
                    className="flex items-center gap-3 rounded-xl border border-white/8 bg-[#121212] p-4 panel-hover">
                    <I size={22} weight="duotone" className="text-[#00E5FF]" />
                    <span className="text-sm font-medium text-white">{o.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ icon: Icon, label, value, color }) {
  return (
    <div className="flex items-center justify-between">
      <span className="flex items-center gap-2 text-sm text-zinc-400"><Icon size={17} weight="duotone" style={{ color }} />{label}</span>
      <span className="font-display font-bold" style={{ color }}>{value}</span>
    </div>
  );
}

function MiniStat({ label, value, to, icon: Icon, warn }) {
  return (
    <Link to={to} className="rounded-xl border border-white/5 bg-black/20 p-3 panel-hover block">
      <Icon size={18} weight="duotone" className={warn ? "text-[#FF3366] mx-auto" : "text-[#00E5FF] mx-auto"} />
      <div className="font-display text-xl font-bold text-white mt-1">{value}</div>
      <div className="text-[11px] text-zinc-500">{label}</div>
    </Link>
  );
}
